import React, { useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import JsBarcode from 'jsbarcode';
import { AuthContext } from '../context/AuthContext';
import Navbar from './Navbar';
import './LabelPrintTest.css';

const formatInr = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(n) || 0);

/** Each sticker: 47.5 mm × 60 mm printable; 3 mm gap on roll between stickers */
const LABEL_WIDTH_CSS = '47.5mm';
const LABEL_HEIGHT_MM = 60;
const ROLL_GAP_MM = 3;

/**
 * "Driver sheet" width + one roll pitch (label + gap). Using 4 in tall pages (~101 mm) caused ~2
 * physical stickers to advance per print; 63 mm matches 60 + 3 mm gap.
 */
const DRIVER_PAGE_W = '2in';
const DRIVER_PITCH_MM = LABEL_HEIGHT_MM + ROLL_GAP_MM;

function barcodeOpts(paperMode) {
  if (paperMode === 'twoByFour') {
    return {
      format: 'CODE128',
      width: 0.95,
      height: 24,
      margin: 0,
      marginTop: 0,
      marginBottom: 0,
      displayValue: true,
      fontSize: 6,
      textMargin: 0
    };
  }
  return {
    format: 'CODE128',
    width: 1,
    height: 28,
    margin: 0,
    marginTop: 0,
    marginBottom: 0,
    displayValue: true,
    fontSize: 6,
    textMargin: 0
  };
}

function clampInt(n, min, max) {
  const v = parseInt(String(n), 10);
  if (Number.isNaN(v)) return min;
  return Math.min(max, Math.max(min, v));
}

function stackHeightMm(count) {
  return count * LABEL_HEIGHT_MM + Math.max(0, count - 1) * ROLL_GAP_MM;
}

export default function LabelPrintTest() {
  const { user } = useContext(AuthContext);
  const svgRefs = useRef([]);
  const [paperMode, setPaperMode] = useState('roll');
  const [productName, setProductName] = useState('Test cake slice');
  const [detailLine, setDetailLine] = useState('Prepared in-store • MRP incl. of all taxes');
  const [barcodeText, setBarcodeText] = useState('001');
  const [price, setPrice] = useState('120');
  const [labelCount, setLabelCount] = useState(1);
  const [showPieceNumbers, setShowPieceNumbers] = useState(true);
  const [barcodeError, setBarcodeError] = useState(null);

  const count = clampInt(labelCount, 1, 99);
  const totalPrintMm = stackHeightMm(count);
  const pitchMm = LABEL_HEIGHT_MM + ROLL_GAP_MM;
  const is2x4 = paperMode === 'twoByFour';

  const driverStackMm = count * DRIVER_PITCH_MM;

  const pageStyle = useMemo(() => {
    if (is2x4) {
      return `@page { size: ${DRIVER_PAGE_W} ${DRIVER_PITCH_MM}mm; margin: 0; }`;
    }
    return `@page { size: ${LABEL_WIDTH_CSS} ${totalPrintMm}mm; margin: 0; }`;
  }, [is2x4, totalPrintMm]);

  useEffect(() => {
    document.body.classList.add('label-print-test-active');
    document.documentElement.classList.add('label-print-test-active');
    return () => {
      document.body.classList.remove('label-print-test-active');
      document.documentElement.classList.remove('label-print-test-active');
    };
  }, []);

  useEffect(() => {
    if (is2x4) document.documentElement.classList.add('label-print-2x4');
    else document.documentElement.classList.remove('label-print-2x4');
    return () => document.documentElement.classList.remove('label-print-2x4');
  }, [is2x4]);

  useEffect(() => {
    if (is2x4) {
      document.documentElement.style.setProperty('--label-print-stack-height', `${driverStackMm}mm`);
      document.documentElement.style.setProperty('--label-print-page-w', DRIVER_PAGE_W);
      document.documentElement.style.setProperty('--label-print-page-h', `${DRIVER_PITCH_MM}mm`);
      document.documentElement.style.setProperty('--label-print-pitch-mm', `${DRIVER_PITCH_MM}mm`);
      document.documentElement.style.setProperty('--label-print-w', LABEL_WIDTH_CSS);
      document.documentElement.style.setProperty('--label-print-h', `${LABEL_HEIGHT_MM}mm`);
      document.documentElement.style.setProperty('--label-print-gap', `${ROLL_GAP_MM}mm`);
    } else {
      document.documentElement.style.setProperty('--label-print-stack-height', `${totalPrintMm}mm`);
      document.documentElement.style.removeProperty('--label-print-page-w');
      document.documentElement.style.removeProperty('--label-print-page-h');
      document.documentElement.style.removeProperty('--label-print-pitch-mm');
      document.documentElement.style.setProperty('--label-print-w', LABEL_WIDTH_CSS);
      document.documentElement.style.setProperty('--label-print-h', `${LABEL_HEIGHT_MM}mm`);
      document.documentElement.style.setProperty('--label-print-gap', `${ROLL_GAP_MM}mm`);
    }
    return () => {
      document.documentElement.style.removeProperty('--label-print-stack-height');
      document.documentElement.style.removeProperty('--label-print-page-w');
      document.documentElement.style.removeProperty('--label-print-page-h');
      document.documentElement.style.removeProperty('--label-print-pitch-mm');
      document.documentElement.style.removeProperty('--label-print-w');
      document.documentElement.style.removeProperty('--label-print-h');
      document.documentElement.style.removeProperty('--label-print-gap');
    };
  }, [is2x4, count, totalPrintMm, driverStackMm]);

  const drawBarcodes = useCallback(() => {
    setBarcodeError(null);
    const text = (barcodeText || '').trim();
    const opts = barcodeOpts(paperMode);
    for (let i = 0; i < count; i++) {
      const svg = svgRefs.current[i];
      if (!svg) continue;
      while (svg.firstChild) svg.removeChild(svg.firstChild);
    }
    if (!text) return;
    try {
      for (let i = 0; i < count; i++) {
        const svg = svgRefs.current[i];
        if (!svg) continue;
        JsBarcode(svg, text, opts);
      }
    } catch (e) {
      setBarcodeError(e?.message || 'Invalid barcode value for Code 128');
    }
  }, [barcodeText, count, paperMode]);

  useEffect(() => {
    drawBarcodes();
  }, [drawBarcodes]);

  const setSvgRef = (index) => (el) => {
    svgRefs.current[index] = el;
  };

  const handlePrint = () => {
    drawBarcodes();
    requestAnimationFrame(() => {
      window.print();
    });
  };

  const labelCard = (i) => (
    <div className="label-print-card label-print-card--readable">
      {showPieceNumbers && count > 1 ? (
        <div className="label-print-piece">
          Piece {i + 1} of {count}
        </div>
      ) : null}
      <div className="label-print-name">{productName.trim() || '—'}</div>
      {detailLine.trim() ? <div className="label-print-detail">{detailLine.trim()}</div> : null}
      <div className="label-print-barcode-wrap">
        <svg
          ref={setSvgRef(i)}
          className="label-print-barcode-svg"
          role="img"
          aria-label={`Product barcode, label ${i + 1} of ${count}`}
        />
      </div>
      <div className="label-print-price">{formatInr(price)}</div>
    </div>
  );

  return (
    <div className={`label-print-page cc-page${is2x4 ? ' label-print-page--2x4' : ''}`}>
      <style>{pageStyle}</style>
      <div className="label-print-no-print">
        <Navbar />
        <header className="cc-page-head">
          <h1 className="cc-page-title">Label print test (browser)</h1>
          <p className="cc-page-lead">
            Each sticker is <strong>47.5 mm × {LABEL_HEIGHT_MM} mm</strong> with <strong>{ROLL_GAP_MM} mm</strong> between
            stickers (pitch <strong>{pitchMm} mm</strong>). <strong>Roll (exact)</strong> is best when the driver accepts a
            47.5 mm-wide strip. <strong>Driver sheet (2 in × 63 mm)</strong> is one print page per sticker: 60 mm artwork
            aligned to the <strong>top</strong> plus a 3 mm white band — this avoids a tall 4 in page using ~<strong>two</strong>{' '}
            stickers per print. If Windows only offers <strong>2 × 4 in</strong>, pick it only if you cannot use a custom
            size; you may still see extra feed. Use <strong>Copies = 1</strong> with quantity here; <strong>100% / actual size</strong>.
          </p>
          <p className="label-print-back">
            <Link to="/pos">← Back to POS</Link>
          </p>
        </header>

        <div className="cc-card label-print-controls">
          <div className="label-print-field-row label-print-field-row--paper">
            <fieldset className="label-print-paper-fieldset">
              <legend>Paper / driver</legend>
              <label className="label-print-radio">
                <input type="radio" name="paperMode" checked={!is2x4} onChange={() => setPaperMode('roll')} />
                Roll — exact 47.5 mm wide strip ({totalPrintMm} mm tall for {count} label{count === 1 ? '' : 's'})
              </label>
              <label className="label-print-radio">
                <input type="radio" name="paperMode" checked={is2x4} onChange={() => setPaperMode('twoByFour')} />
                Driver sheet — 2 in wide × 63 mm per page (47.5×60 mm from top + 3 mm gap; not 4 in tall)
              </label>
            </fieldset>
          </div>
          <div className="label-print-field-row label-print-field-row--four">
            <label>
              Product name
              <input
                className="form-control"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                maxLength={56}
              />
            </label>
            <label>
              Detail lines (small text under title)
              <input
                className="form-control"
                value={detailLine}
                onChange={(e) => setDetailLine(e.target.value)}
                maxLength={90}
                placeholder="e.g. weight, batch, tax line"
              />
            </label>
            <label>
              Barcode (Code 128)
              <input className="form-control" value={barcodeText} onChange={(e) => setBarcodeText(e.target.value)} />
            </label>
            <label>
              Price (INR)
              <input
                className="form-control"
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </label>
          </div>
          <div className="label-print-field-row label-print-field-row--count">
            <label>
              Number of labels (one print{is2x4 ? '; one 2 in×63 mm page each' : '; stacked on roll'})
              <input
                className="form-control"
                type="number"
                min={1}
                max={99}
                value={labelCount}
                onChange={(e) => setLabelCount(e.target.value)}
              />
            </label>
            <label className="label-print-checkbox">
              <input
                type="checkbox"
                checked={showPieceNumbers}
                onChange={(e) => setShowPieceNumbers(e.target.checked)}
                disabled={count < 2}
              />
              Show piece number when printing more than one
            </label>
          </div>
          {barcodeError ? <p className="label-print-error">{barcodeError}</p> : null}
          <div className="label-print-actions">
            <button type="button" className="btn btn-primary" onClick={handlePrint} disabled={!!barcodeError || !barcodeText.trim()}>
              Print {count === 1 ? '1 label' : `${count} labels`}
            </button>
          </div>
        </div>
      </div>

      <div className="label-print-preview-wrap label-print-no-print">
        <p className="label-print-preview-caption">
          {is2x4
            ? `Preview — gray frame is ${DRIVER_PAGE_W} × ${DRIVER_PITCH_MM} mm (one pitch); label from top; dashed area 47.5×${LABEL_HEIGHT_MM} mm.`
            : `Preview — dashed inner area is 47.5×${LABEL_HEIGHT_MM} mm; thin strip = ${ROLL_GAP_MM} mm roll gap. Strip ${totalPrintMm} mm tall.`}
        </p>
      </div>

      <div className="label-print-sheets" aria-label="Label previews">
        {Array.from({ length: count }, (_, i) => (
          <div className={`label-print-preview-unit${is2x4 ? ' label-print-preview-unit--2x4' : ''}`} key={i}>
            {is2x4 ? (
              <div className="label-print-driver-sheet">
                <div className="label-print-one-sheet">{labelCard(i)}</div>
                <div className="label-print-driver-post-gap" aria-hidden />
              </div>
            ) : (
              <>
                <div className="label-print-one-sheet">{labelCard(i)}</div>
                {i < count - 1 ? (
                  <div
                    className="label-print-roll-gap"
                    aria-hidden
                    title={`${ROLL_GAP_MM} mm gap on roll after sticker ${i + 1}`}
                  />
                ) : null}
              </>
            )}
          </div>
        ))}
      </div>

      {user?.role ? (
        <p className="label-print-meta label-print-no-print">
          Signed in as {user.username} ({user.role})
        </p>
      ) : null}
    </div>
  );
}
